import { FIELD_VALUE_TYPE, FILTERS_SA_TYPE } from '@constants'
import { POSTObjectDataFilter, SearchAssistantBodyItem } from '@types'

// Утилиты
const stringHasText = (s: unknown): s is string => typeof s === 'string' && s.trim().length > 0

const isEmptyArray = <T>(a: T[] | undefined | null): a is undefined | null =>
  !Array.isArray(a) || a.length === 0

const nonNull = <T>(v: T | null | undefined): v is T => v !== null && v !== undefined
const isNull = (v: unknown): v is null | undefined => v === null || v === undefined

// Форматирование даты в UTC: yyyy-MM-dd'T'HH:mm:ss.SSS'Z'
const pad = (n: number, size = 2) => String(n).padStart(size, '0')
const formatDateUTC = (dateInput: string | number | Date): string => {
  const d = new Date(dateInput)
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid date: ${String(dateInput)}`)
  }

  return (
    d.getUTCFullYear() +
    '-' +
    pad(d.getUTCMonth() + 1) +
    '-' +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    ':' +
    pad(d.getUTCMinutes()) +
    ':' +
    pad(d.getUTCSeconds()) +
    '.' +
    pad(d.getUTCMilliseconds(), 3) +
    'Z'
  )
}

export function getRSqlBySearch(values: POSTObjectDataFilter): string | null {
  if (isNull(values) || isEmptyArray(values)) {
    return null
  }
  const valuesList: string[] = []
  for (const value of values!) {
    valuesList.push(...convertValue(value))
  }

  return valuesList
    .filter(x => stringHasText(x))
    .map(x => `(${x})`)
    .join(';')
}

// Парсинг значений поиска
function getValues(searchValue: string): string[] {
  if (!stringHasText(searchValue)) {
    return []
  }

  return [searchValue.trim()]
}

export function getFilterByQuickSearch(
  searchRule: string,
  searchValue: string,
  sortKeysForQuickSearch: string[]
): string | null | undefined {
  if (
    (!stringHasText(searchValue) &&
      nonNull(searchRule) &&
      searchRule !== FILTERS_SA_TYPE.EMPTY &&
      searchRule !== FILTERS_SA_TYPE.NOT_EMPTY) ||
    !stringHasText(searchRule) ||
    isNull(sortKeysForQuickSearch)
  ) {
    return undefined
  }

  const filters: string[] = []
  const values = getValues(searchValue)

  sortKeysForQuickSearch.forEach(fieldName => {
    filters.push(getFilter(fieldName, searchRule as FILTERS_SA_TYPE, values, false, false) ?? '')
  })

  return filters
    .filter(stringHasText)
    .map(f => `(${f})`)
    .join(',')
}

/**
 * Объединяет несколько RSQL-фильтров через AND (;).
 * Каждую часть оборачивает в скобки, чтобы сохранить приоритет при наличии внутренних , (OR).
 */
export function mergeRsqlFilters(...parts: (string | null | undefined)[]): string | undefined {
  const filtered = parts.filter((p): p is string => stringHasText(p))

  return filtered.length === 0 ? undefined : filtered.map(p => `(${p})`).join(';')
}

function getValueRequestFilter(
  filterPattern: string,
  fieldName: string,
  valuePattern: string,
  values: string[]
): string {
  return values
    .map(v => valuePattern.replace('%s', v))
    .map(v => `"${v}"`)
    .map(v => filterPattern.replace('%s', fieldName).replace('%s', v))
    .join(',')
}

function getArrayValueRequestFilter(
  filterPattern: string,
  fieldName: string,
  valuePattern: string,
  values: string[]
): string {
  const joined = values
    .map(v => valuePattern.replace('%s', v))
    .map(v => (v.includes(' ') ? `"${v}"` : v))
    .join(',')

  return filterPattern.replace('%s', fieldName).replace('%s', joined)
}

function getGroupValueRequestFilter(
  filterPattern: string,
  fieldName: string,
  values: string[]
): string {
  return filterPattern.replace('%s', fieldName).replace('%s', values.join(','))
}

function getFilter(
  fieldName: string,
  searchRule: FILTERS_SA_TYPE,
  values: string[],
  isArrayField: boolean,
  isGroupRelated: boolean
): string | null {
  if (
    isEmptyArray(values) &&
    ![FILTERS_SA_TYPE.EMPTY, FILTERS_SA_TYPE.NOT_EMPTY].includes(searchRule)
  ) {
    return null
  }
  if (isGroupRelated) {
    return getGroupValueRequestFilter('%s=ingroup=(%s)', fieldName, values)
  } else if (isArrayField) {
    switch (searchRule) {
      case FILTERS_SA_TYPE.CONTAIN:
        return getArrayValueRequestFilter('%s=contain=(%s)', fieldName, '%s', values)
      case FILTERS_SA_TYPE.NOT_CONTAIN:
        return getArrayValueRequestFilter('%s=notcontain=(%s)', fieldName, '%s', values)
      case FILTERS_SA_TYPE.EQUAL:
        return getArrayValueRequestFilter('%s=in=(%s)', fieldName, '%s', values)
      case FILTERS_SA_TYPE.NOT_EQUAL:
        return getArrayValueRequestFilter('%s!=(%s)', fieldName, '%s', values)
      case FILTERS_SA_TYPE.EMPTY:
        return `${fieldName}=is= empty`
      case FILTERS_SA_TYPE.NOT_EMPTY:
        return `${fieldName}=isnot= empty`
      default:
        return ''
    }
  } else {
    switch (searchRule) {
      case FILTERS_SA_TYPE.CONTAIN:
        return getValueRequestFilter('%s==%s', fieldName, '*%s*', values)
      case FILTERS_SA_TYPE.NOT_CONTAIN:
        return getValueRequestFilter('%s!=%s', fieldName, '*%s*', values)
      case FILTERS_SA_TYPE.EQUAL:
        return getValueRequestFilter('%s==%s', fieldName, '%s', values)
      case FILTERS_SA_TYPE.NOT_EQUAL:
        return getValueRequestFilter('%s!=%s', fieldName, '%s', values)
      case FILTERS_SA_TYPE.STARTS_WITH:
        return getValueRequestFilter('%s==%s', fieldName, '%s*', values)
      case FILTERS_SA_TYPE.ENDS_WITH:
        return getValueRequestFilter('%s==%s', fieldName, '*%s', values)
      case FILTERS_SA_TYPE.EMPTY:
        return `${fieldName}=is= empty`
      case FILTERS_SA_TYPE.NOT_EMPTY:
        return `${fieldName}=isnot= empty`
      case FILTERS_SA_TYPE.MORE:
        return getValueRequestFilter('%s=gt=(%s)', fieldName, '%s', values)
      case FILTERS_SA_TYPE.MORE_OR_EQUAL:
        return getValueRequestFilter('%s=ge=(%s)', fieldName, '%s', values)
      case FILTERS_SA_TYPE.LESS:
        return getValueRequestFilter('%s=lt=(%s)', fieldName, '%s', values)
      case FILTERS_SA_TYPE.LESS_OR_EQUAL:
        return getValueRequestFilter('%s=le=(%s)', fieldName, '%s', values)
      default:
        return ''
    }
  }
}

function equalValue(value: SearchAssistantBodyItem): string[] {
  const rsqlRequests: string[] = []
  const v = value?.value
  if (v && Array.isArray(v.JSONValue)) {
    const list = v.JSONValue.filter((x): x is unknown => x !== null && x !== undefined).map(x =>
      String(x)
    )
    rsqlRequests.push(
      getFilter(
        value.valueName,
        value.searchRule as FILTERS_SA_TYPE,
        list,
        Boolean(v.isArray),
        Boolean(value.isGroupRelated)
      ) ?? ''
    )
  }

  return rsqlRequests.filter(stringHasText)
}

function dateValue(value: SearchAssistantBodyItem): string[] {
  const rsqlRequests: string[] = []
  const v = value?.value
  if (!v || !Array.isArray(v.JSONValue)) return rsqlRequests

  const rangeDate = v.JSONValue as (string | null | undefined)[]
  const fieldType = value.fieldType

  if (fieldType === 'exact_value') {
    const base = rangeDate[0]
    if (!stringHasText(base)) {
      throw new Error(`Invalid date: ${String(base)}`)
    }
    const d = formatDateUTC(base)
    rsqlRequests.push(`${value.valueName}=='${d}'`)

    return rsqlRequests
  }

  if (rangeDate.length !== 2 && rangeDate.length !== 3) {
    throw new Error(`Incorrect date format ${JSON.stringify(rangeDate)}`)
  }

  if (fieldType === 'relative_value') {
    const baseStr = rangeDate[0]
    if (!stringHasText(baseStr)) {
      throw new Error(`Invalid date: ${String(baseStr)}`)
    }

    const baseDate = new Date(baseStr)
    if (Number.isNaN(baseDate.getTime())) {
      throw new Error(`Invalid date: ${String(baseStr)}`)
    }

    const startTime = new Date(baseDate.getTime())
    const endTime = new Date(baseDate.getTime())

    const startShift = (rangeDate[1] ?? '') as string
    const endShift = (rangeDate[2] ?? '') as string

    const addHours = (d: Date, hours: string) => {
      const nd = new Date(d.getTime())
      nd.setUTCHours(nd.getUTCHours() + Number(hours))

      return nd
    }

    if (!stringHasText(startShift) && stringHasText(endShift)) {
      endTime.setTime(addHours(endTime, endShift).getTime())
    } else if (stringHasText(startShift) && !stringHasText(endShift)) {
      startTime.setTime(addHours(startTime, startShift).getTime())
    } else if (stringHasText(startShift) && stringHasText(endShift)) {
      startTime.setTime(addHours(startTime, startShift).getTime())
      endTime.setTime(addHours(endTime, endShift).getTime())
    }

    if (stringHasText(startShift)) {
      rsqlRequests.push(`${value.valueName}>=${formatDateUTC(startTime)}`)
    }
    if (stringHasText(endShift)) {
      rsqlRequests.push(`${value.valueName}<=${formatDateUTC(endTime)}`)
    }
  }

  if (fieldType === 'range_of_values' || fieldType === 'daily_range') {
    if (nonNull(rangeDate[0])) {
      rsqlRequests.push(`${value.valueName}>=${rangeDate[0]}`)
    }
    if (nonNull(rangeDate[1])) {
      rsqlRequests.push(`${value.valueName}<=${rangeDate[1]}`)
    }
  }

  return rsqlRequests
}

function convertValue(value: SearchAssistantBodyItem): string[] {
  const rsqlRequests: string[] = []
  if (nonNull(value?.valueType) && value.valueType === FIELD_VALUE_TYPE.DATETIME) {
    rsqlRequests.push(...dateValue(value))
  } else {
    rsqlRequests.push(...equalValue(value))
  }

  return rsqlRequests
}
